import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getAlternatePath } from '../config/routes';
import { Menu, X, ChevronDown, User, LogOut, Calendar, LayoutDashboard } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_arce-escuela/artifacts/yy55ojqv_arcelogo.jpeg';

// Helper: get language-prefixed path
function lp(language, path) {
  return `/${language}${path}`;
}

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const switchLanguage = () => {
    const target = language === 'es' ? 'en' : 'es';
    const newPath = getAlternatePath(location.pathname, target);
    toggleLanguage(target);
    navigate(newPath);
  };

  const getDashboardPath = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin';
    if (user.role === 'student') return '/student';
    if (user.role === 'teacher') return '/teacher';
    return '/dashboard';
  };

  const getDashboardLabel = () => {
    if (!user) return '';
    const labels = {
      admin: language === 'es' ? 'Panel Admin' : 'Admin Panel',
      student: language === 'es' ? 'Mi Dashboard' : 'My Dashboard',
      teacher: language === 'es' ? 'Mi Dashboard' : 'My Dashboard',
      parent: language === 'es' ? 'Mi Cuenta' : 'My Account'
    };
    return labels[user.role] || (language === 'es' ? 'Mi Cuenta' : 'My Account');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <>
      {/* Top Bar CTA */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#E63946] text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
          <Calendar className="h-4 w-4" />
          <span className="text-sm font-medium">
            {language === 'es' ? '¿Quieres conocernos?' : 'Want to meet us?'}
          </span>
          <a 
            href="https://docs.google.com/forms/d/e/1FAIpQLSfPiL3018J22-HMqQQwdU6od-JDFeBF9tUFBEu_J4ZH96nWBQ/viewform"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold underline underline-offset-2 hover:no-underline"
            data-testid="top-bar-cta"
          >
            {language === 'es' ? 'Prueba una clase gratis' : 'Try a free class'}
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <header className="fixed top-10 left-0 right-0 z-50 glass-nav">
        <nav className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo - Bigger */}
            <Link to={lp(language, '/')} className="flex items-center" data-testid="navbar-logo">
              <img src={LOGO_URL} alt="Colegio ARCE" className="h-16 w-auto" />
            </Link>

          {/* Desktop Navigation - Horizontal */}
          <div className="hidden lg:flex items-center gap-6">
            <Link 
              to={lp(language, '/')} 
              className="text-[#1D1D1F] hover:text-[#E63946] transition-colors font-medium text-sm"
              data-testid="nav-home"
            >
              {t('nav.home')}
            </Link>

            {/* About Us Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-[#1D1D1F] hover:text-[#E63946] transition-colors font-medium text-sm" data-testid="nav-about-dropdown">
                {t('nav.aboutUs')}
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white rounded-xl shadow-lg border border-black/5 p-2">
                <DropdownMenuItem asChild>
                  <Link to={lp(language, language === 'es' ? '/quienes-somos' : '/about')} className="cursor-pointer px-4 py-2 hover:bg-[#F5F5F7] rounded-lg text-sm" data-testid="nav-who-we-are">
                    {t('nav.whoWeAre')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={lp(language, language === 'es' ? '/filosofia' : '/philosophy')} className="cursor-pointer px-4 py-2 hover:bg-[#F5F5F7] rounded-lg text-sm" data-testid="nav-philosophy">
                    {t('nav.philosophy')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={lp(language, language === 'es' ? '/equipo' : '/team')} className="cursor-pointer px-4 py-2 hover:bg-[#F5F5F7] rounded-lg text-sm" data-testid="nav-team">
                    {t('nav.team')}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Escolaridad Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-[#1D1D1F] hover:text-[#E63946] transition-colors font-medium text-sm" data-testid="nav-escolaridad-dropdown">
                {t('nav.escolaridad')}
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white rounded-xl shadow-lg border border-black/5 p-2">
                <DropdownMenuItem asChild>
                  <Link to={lp(language, language === 'es' ? '/programas' : '/programs')} className="cursor-pointer px-4 py-2 hover:bg-[#F5F5F7] rounded-lg text-sm" data-testid="nav-courses">
                    {t('nav.coursesLevels')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={lp(language, language === 'es' ? '/metodologia' : '/methodology')} className="cursor-pointer px-4 py-2 hover:bg-[#F5F5F7] rounded-lg text-sm" data-testid="nav-methodology">
                    {t('nav.methodology')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={lp(language, language === 'es' ? '/calendario' : '/calendar')} className="cursor-pointer px-4 py-2 hover:bg-[#F5F5F7] rounded-lg text-sm" data-testid="nav-calendar">
                    {t('nav.calendar')}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to={lp(language, language === 'es' ? '/tarifas' : '/pricing')} 
              className="text-[#1D1D1F] hover:text-[#E63946] transition-colors font-medium text-sm"
              data-testid="nav-pricing"
            >
              {t('nav.pricing')}
            </Link>

            <Link 
              to={lp(language, '/blog')} 
              className="text-[#1D1D1F] hover:text-[#E63946] transition-colors font-medium text-sm"
              data-testid="nav-blog"
            >
              {t('nav.blog')}
            </Link>

            <Link 
              to={lp(language, language === 'es' ? '/contacto' : '/contact')} 
              className="text-[#1D1D1F] hover:text-[#E63946] transition-colors font-medium text-sm"
              data-testid="nav-contact"
            >
              {t('nav.contact')}
            </Link>
          </div>

          {/* Right side: Language toggle, Auth, CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Toggle */}
            <div className="lang-toggle" data-testid="language-toggle">
              <button
                onClick={switchLanguage}
                className={language === 'es' ? 'active' : ''}
                data-testid="lang-es"
              >
                ES
              </button>
              <button
                onClick={switchLanguage}
                className={language === 'en' ? 'active' : ''}
                data-testid="lang-en"
              >
                EN
              </button>
            </div>

            {/* Auth */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#F5F5F7] hover:bg-[#EBEBEF] transition-colors text-sm" data-testid="user-menu">
                  <User className="h-4 w-4" />
                  <span className="font-medium">{user.name?.split(' ')[0]}</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white rounded-xl shadow-lg border border-black/5 p-2">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardPath()} className="cursor-pointer px-4 py-2 hover:bg-[#F5F5F7] rounded-lg flex items-center gap-2 text-sm" data-testid="nav-dashboard">
                      <LayoutDashboard className="h-4 w-4" />
                      {getDashboardLabel()}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer px-4 py-2 hover:bg-[#F5F5F7] rounded-lg flex items-center gap-2 text-red-600 text-sm" data-testid="nav-logout">
                    <LogOut className="h-4 w-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#F5F5F7] hover:bg-[#EBEBEF] transition-colors text-sm font-medium text-[#1D1D1F]"
                data-testid="nav-portal-login"
              >
                <User className="h-4 w-4" />
                {language === 'es' ? 'Iniciar Sesión' : 'Login'}
              </Link>
            )}

            {/* CTA */}
            <Link
              to={`/login?redirect=/${language}/${language === 'es' ? 'inscripciones' : 'enrollment'}`}
              className="bg-[#E63946] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#D62828] transition-colors shadow-sm"
              data-testid="nav-register-cta"
            >
              {t('nav.register')}
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 py-4 border-t border-black/5">
            <div className="flex flex-col gap-4">
              <Link to={lp(language, '/')} className="text-[#1D1D1F] font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-home">
                {t('nav.home')}
              </Link>
              <Link to={lp(language, language === 'es' ? '/quienes-somos' : '/about')} className="text-[#1D1D1F] font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-about">
                {t('nav.aboutUs')}
              </Link>
              <Link to={lp(language, language === 'es' ? '/programas' : '/programs')} className="text-[#1D1D1F] font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-courses">
                {t('nav.coursesLevels')}
              </Link>
              <Link to={lp(language, language === 'es' ? '/tarifas' : '/pricing')} className="text-[#1D1D1F] font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-pricing">
                {t('nav.pricing')}
              </Link>
              <Link to={lp(language, '/blog')} className="text-[#1D1D1F] font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-blog">
                {t('nav.blog')}
              </Link>
              <Link to={lp(language, language === 'es' ? '/contacto' : '/contact')} className="text-[#1D1D1F] font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-contact">
                {t('nav.contact')}
              </Link>

              <div className="pt-4 border-t border-black/5 flex items-center gap-4">
                <div className="lang-toggle">
                  <button onClick={() => { switchLanguage(); setMobileMenuOpen(false); }} className={language === 'es' ? 'active' : ''}>ES</button>
                  <button onClick={() => { switchLanguage(); setMobileMenuOpen(false); }} className={language === 'en' ? 'active' : ''}>EN</button>
                </div>

                {user ? (
                  <>
                    <Link to={getDashboardPath()} className="text-[#1D1D1F] font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-dashboard">
                      <LayoutDashboard className="h-4 w-4" />
                      {getDashboardLabel()}
                    </Link>
                    <button onClick={handleLogout} className="text-red-600 font-medium flex items-center gap-2" data-testid="mobile-logout">
                      <LogOut className="h-4 w-4" />
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <Link to="/login" className="text-[#1D1D1F] font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-portal">
                    <User className="h-4 w-4" />
                    {language === 'es' ? 'Iniciar Sesión' : 'Login'}
                  </Link>
                )}
              </div>

              <Link
                to={`/login?redirect=/${language}/${language === 'es' ? 'inscripciones' : 'enrollment'}`}
                className="btn-primary text-center"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-register"
              >
                {t('nav.register')}
              </Link>
            </div>
          </div>
        )}
        </nav>
      </header>
    </>
  );
}
